<template>
  <div class="cloud-storage-container">
    <vue-element-loading
      :active="appLoading"
      :is-full-screen="true"
      background-color="#FFFFFF"
      color="#005f32"
      spinner="bar-fade-scale"
    />
    
    <v-snackbar v-model="showsnackbar" :color="snackbarColor" right top>
      <v-layout wrap justify-center>
        <v-flex text-left class="align-self-center">{{ msg }}</v-flex>
        <v-flex text-right>
          <v-btn small :ripple="false" text @click="showsnackbar = false">
            <v-icon style="color: white">mdi-close</v-icon>
          </v-btn>
        </v-flex>
      </v-layout>
    </v-snackbar>

    <v-layout wrap class="pa-4">
      <v-flex xs12>
        <v-card class="elevation-1 rounded-lg">
          <!-- Toolbar -->
          <v-toolbar flat color="white" class="rounded-t-lg border-bottom">
            <v-btn icon @click="$router.go(-1)" class="mr-2">
              <v-icon>mdi-arrow-left</v-icon>
            </v-btn>
            <v-toolbar-title class="font-weight-bold" style="color: #2c3e50;">
              {{ employeeName ? employeeName + "'s Documents" : "Employee Documents" }}
            </v-toolbar-title>
            <v-spacer></v-spacer>
            <v-btn color="#005f32" dark class="rounded-md font-weight-bold px-4" depressed @click="uploadDialog = true">
              <v-icon left>mdi-cloud-upload</v-icon>
              Upload Document
            </v-btn>
          </v-toolbar>

          <v-divider></v-divider>

          <!-- Content Area -->
          <v-card-text class="grey lighten-5 py-5" style="min-height: 400px;">
            <v-layout wrap v-if="documentList.length === 0 && !appLoading" justify-center align-center fill-height>
              <div class="text-center pa-5 my-10">
                <v-icon size="80" color="grey lighten-2">mdi-folder-open-outline</v-icon>
                <h3 class="grey--text text--darken-1 mt-4">No documents uploaded yet</h3>
                <p class="grey--text">Click the Upload Document button to add files.</p>
              </div>
            </v-layout>

            <div v-else>
              <v-data-table
                :headers="headers"
                :items="documentList"
                class="elevation-0"
                :items-per-page="10"
                hide-default-footer
                :page.sync="currentPage"
                @page-count="pages = $event"
              >
                <template v-slot:item.docName="{ item }">
                  <div class="d-flex align-center py-2">
                    <v-icon :color="getFileIconColor(item.docUrl)" class="mr-3">{{ getFileIcon(item.docUrl) }}</v-icon>
                    <span class="font-weight-bold" style="color: #2c3e50">{{ item.docName }}</span>
                  </div>
                </template>
                
                <template v-slot:item.docUrl="{ item }">
                  <span class="grey--text">{{ item.docUrl }}</span>
                </template>
                
                <template v-slot:item.actions="{ item }">
                  <v-btn small text color="primary" class="mr-3 font-weight-bold" @click="viewDocument(item.docUrl)">
                    <v-icon left size="16">mdi-eye</v-icon> VIEW
                  </v-btn>
                  <v-btn icon small color="error" @click="confirmDelete(item)">
                    <v-icon size="18">mdi-delete</v-icon>
                  </v-btn>
                </template>
              </v-data-table>
              
              <div class="text-center pt-4" v-if="pages > 1">
                <v-pagination prev-icon="mdi-menu-left" next-icon="mdi-menu-right" :length="pages" :total-visible="7"
                  v-model="currentPage" color="#005f32"></v-pagination>
              </div>
            </div>
          </v-card-text>
        </v-card>
      </v-flex>
    </v-layout>

    <!-- Upload Dialog -->
    <v-dialog v-model="uploadDialog" max-width="500px" persistent>
      <v-card class="rounded-lg">
        <v-card-title class="headline pb-2">
          Upload Document
          <v-spacer></v-spacer>
          <v-btn icon @click="closeUploadDialog"><v-icon>mdi-close</v-icon></v-btn>
        </v-card-title>
        <v-card-text class="pt-4">
          <v-form ref="uploadForm" v-model="validForm">
            <v-text-field
              v-model="uploadData.docTitle"
              label="Document Title"
              outlined
              dense
              color="#005f32"
              :rules="[v => !!v || 'Title is required']"
              placeholder="e.g. Aadhar Card, Resume"
            ></v-text-field>

            <div class="upload-box mt-3" @click="$refs.fileInput.click()">
              <v-icon size="40" color="#005f32" class="mb-2">mdi-cloud-upload-outline</v-icon>
              <div v-if="!uploadData.file" class="grey--text text--darken-1">
                Click to browse file
              </div>
              <div v-else class="font-weight-bold" style="color: #005f32;">
                {{ uploadData.file.name }}
              </div>
            </div>
            <input
              type="file"
              ref="fileInput"
              style="display: none"
              @change="handleFileUpload"
            />
          </v-form>
        </v-card-text>
        <v-card-actions class="px-6 pb-6 pt-0">
          <v-spacer></v-spacer>
          <v-btn color="grey darken-1" text @click="closeUploadDialog">Cancel</v-btn>
          <v-btn color="#005f32" dark depressed @click="uploadDocument" :disabled="!validForm || !uploadData.file">
            Upload
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Delete Confirmation -->
    <v-dialog v-model="deleteDialog" max-width="400px" persistent>
      <v-card class="rounded-lg">
        <v-card-title class="headline error--text">Delete Document</v-card-title>
        <v-card-text class="pt-3 text-body-1">
          Are you sure you want to delete <b>{{ docToDelete ? docToDelete.docName : '' }}</b>? This action cannot be undone.
        </v-card-text>
        <v-card-actions class="px-6 pb-6">
          <v-spacer></v-spacer>
          <v-btn color="grey darken-1" text @click="deleteDialog = false">Cancel</v-btn>
          <v-btn color="error" depressed @click="deleteDocument">Delete</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import axios from "axios";

export default {
  data() {
    return {
      appLoading: false,
      employeeId: null,
      employeeName: "",
      documentList: [],
      currentPage: 1,
      pages: 0,
      headers: [
        { text: 'Document Name', value: 'docName', align: 'start' },
        { text: 'File Name', value: 'docUrl' },
        { text: 'Actions', value: 'actions', sortable: false, align: 'end' }
      ],
      
      showsnackbar: false,
      msg: "",
      snackbarColor: "black",
      
      uploadDialog: false,
      validForm: false,
      uploadData: {
        docTitle: "",
        file: null
      },
      
      deleteDialog: false,
      docToDelete: null
    };
  },
  mounted() {
    this.employeeId = this.$route.query.id;
    if (this.employeeId) {
      this.fetchEmployeeDetails();
      this.fetchDocuments();
    } else {
      this.showMessage("Employee ID not found", "error");
    }
  },
  methods: {
    showMessage(msg, color = "black") {
      this.msg = msg;
      this.snackbarColor = color;
      this.showsnackbar = true;
    },
    
    getFileIcon(filename) {
      if (!filename) return "mdi-file-document-outline";
      const ext = filename.split('.').pop().toLowerCase();
      if (['pdf'].includes(ext)) return "mdi-file-pdf-box";
      if (['jpg', 'jpeg', 'png', 'gif'].includes(ext)) return "mdi-image";
      if (['doc', 'docx'].includes(ext)) return "mdi-file-word-box";
      if (['xls', 'xlsx'].includes(ext)) return "mdi-file-excel-box";
      return "mdi-file-document-outline";
    },
    
    getFileIconColor(filename) {
      if (!filename) return "grey";
      const ext = filename.split('.').pop().toLowerCase();
      if (['pdf'].includes(ext)) return "red";
      if (['jpg', 'jpeg', 'png', 'gif'].includes(ext)) return "blue";
      if (['doc', 'docx'].includes(ext)) return "blue darken-2";
      if (['xls', 'xlsx'].includes(ext)) return "green darken-1";
      return "grey darken-1";
    },
    
    viewDocument(filename) {
      if (filename) {
        window.open('https://wtiadmin.in/i/' + filename, '_blank');
      }
    },
    
    fetchEmployeeDetails() {
      axios({
        url: "/employee/details/view",
        method: "GET",
        params: { id: this.employeeId },
        headers: { "x-auth-token": localStorage.getItem("token") }
      }).then(response => {
        if (response.data && response.data.data) {
          const emp = response.data.data;
          this.employeeName = `${emp.firstName || ''} ${emp.lastName || ''}`.trim();
        }
      }).catch(err => console.log(err));
    },
    
    fetchDocuments() {
      this.appLoading = true;
      axios({
        url: `/employeedocs/view/${this.employeeId}`,
        method: "GET",
        headers: { "x-auth-token": localStorage.getItem("token") }
      })
      .then(response => {
        this.appLoading = false;
        if (response.data.status) {
          this.documentList = response.data.doc || [];
        } else {
          this.documentList = [];
        }
      })
      .catch(err => {
        this.appLoading = false;
        console.log(err);
        this.documentList = [];
      });
    },
    
    handleFileUpload(e) {
      if (e.target.files && e.target.files.length > 0) {
        this.uploadData.file = e.target.files[0];
      }
    },
    
    closeUploadDialog() {
      this.uploadDialog = false;
      this.uploadData = { docTitle: "", file: null };
      if (this.$refs.uploadForm) this.$refs.uploadForm.resetValidation();
      if (this.$refs.fileInput) this.$refs.fileInput.value = "";
    },
    
    async uploadDocument() {
      if (!this.uploadData.docTitle || !this.uploadData.file) return;
      
      const formData = new FormData();
      formData.append('docTitle', this.uploadData.docTitle);
      formData.append('documentUrl', this.uploadData.file);
      
      this.appLoading = true;
      try {
        const res = await axios({
          url: `/employeedocs/upload/${this.employeeId}`,
          method: 'POST',
          headers: {
            "x-auth-token": localStorage.getItem("token"),
            "Content-Type": "multipart/form-data"
          },
          data: formData
        });
        
        this.appLoading = false;
        this.closeUploadDialog();
        this.showMessage(res.data.msg || "Document uploaded successfully", "success");
        this.fetchDocuments();
      } catch (err) {
        this.appLoading = false;
        this.showMessage("Failed to upload document", "error");
        console.log(err);
      }
    },
    
    confirmDelete(doc) {
      this.docToDelete = doc;
      this.deleteDialog = true;
    },
    
    deleteDocument() {
      if (!this.docToDelete || !this.docToDelete._id) return;
      
      this.appLoading = true;
      axios({
        url: `/employeedocs/delete/${this.docToDelete._id}`,
        method: "DELETE",
        headers: { "x-auth-token": localStorage.getItem("token") }
      })
      .then(response => {
        this.appLoading = false;
        this.deleteDialog = false;
        this.docToDelete = null;
        this.showMessage(response.data.msg || "Document deleted", "success");
        this.fetchDocuments();
      })
      .catch(err => {
        this.appLoading = false;
        this.deleteDialog = false;
        this.showMessage("Failed to delete document", "error");
        console.log(err);
      });
    }
  }
};
</script>

<style scoped>
.cloud-storage-container {
  background-color: transparent;
}
.document-card {
  transition: transform 0.2s ease, box-shadow 0.2s ease;
  border: 1px solid #eaeaea;
  background-color: white;
}
.document-card:hover {
  transform: translateY(-4px);
  box-shadow: 0 8px 16px rgba(0,0,0,0.08) !important;
  border-color: #d1d5db;
}
.showLine {
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  width: 100%;
}
.border-bottom {
  border-bottom: 1px solid #eaeaea !important;
}
.upload-box {
  border: 2px dashed #005f32;
  border-radius: 8px;
  padding: 30px;
  text-align: center;
  cursor: pointer;
  background-color: #f2f7f4;
  transition: all 0.3s ease;
}
.upload-box:hover {
  background-color: #e6f0ec;
  border-color: #005f32;
  border-style: solid;
}
</style>
